import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { logEvent } from 'firebase/analytics';

import NavData from '@/config/data/nav/nav_data.json';
import { programTvMainList, getNewsList, vodList, episodeList } from '@/network/apis/apiUri';
import useAxios from '@/network/useAxios';
import useContent from '@/localization/data_export';
import analytics from '../../../firebase';
import '../css/mainSlider.scss';
import VideoThumbnail from '@/components/video-thumnail';

const ASIDE_TYPES = {
  NEWS_RECENT_VIDEO: 'full_video',
  NEWS_TOPSTORY_LIST: 'news_topstorys',
  VOD: 'vod',
  EPISODE: 'episode',
};

function MainSlider() {
  const anchorTitle = useContent(['global', 'anchor', 'title']);
  const { mode } = useSelector((state) => state);

  const mainSlide = useRef(null);
  const rightSlideRef = useRef([]);

  let nav = NavData;

  const [mainBg, setMainBg] = useState([]);
  const [mainSlideBg, setMainSlideBg] = useState('');

  const [mainListItem] = useAxios(programTvMainList, [], { lan_code: mode, type: 'main', is_open_allow: true }, true);
  const { data: slideData } = mainListItem;

  const colorChange = (e) => {
    mainBg?.forEach((data, index) => {
      mainSlide.current.swiper.activeIndex === index && setMainSlideBg(data);
    });
    if (mainSlide.current.swiper.activeIndex === mainBg.length + 1) {
      setMainSlideBg(mainBg[0]);
    }
  };

  const fetchAsideData = async (position, slideItem) => {
    const mediaType = slideItem?.description?.[0]?.text;
    const itemType = position === 'left' ? slideItem.sub_title.left_menu_type : slideItem.sub_title.right_menu_type;

    const rightCountPlus =
      slideItem.sub_title.left_menu_type === slideItem.sub_title.right_menu_type && position === 'right' ? 2 : 0;

    const leftCount = 2;
    const rightCount = 10 + rightCountPlus;

    const count = position === 'left' ? leftCount : rightCount;

    switch (itemType) {
      case ASIDE_TYPES.NEWS_RECENT_VIDEO:
        const newsVideos = await episodeList({
          lan_code: mode,
          is_news_allow: true,
          page_info: { row_count: 2, number: 0 },
        });
        return newsVideos.data.item.map((item) => ({
          id: item.episode_id,
          title: item.title,
          link: nav.NEWS_VIDEO.path + `/${item.episode_id}`,
          thumbnail: item.image_url,
          duration: item.duration,
        }));
      case ASIDE_TYPES.NEWS_TOPSTORY_LIST:
        const newsList = await getNewsList({
          lan_code: mode,
          is_list_allow: false,
          is_topstory: true,
          page_info: {
            row_count: 3,
            number: 0,
          },
        });
        return newsList.data.item.map((item) => ({
          id: item.news_id,
          title: item.title.find((t) => t.lan_code === mode).text,
          link: nav.NEWS_VIEW.path + `?id=${item.news_id}`,
          thumbnail: item.image.url,
          duration: item.duration,
        }));
      case ASIDE_TYPES.VOD:
        const vod = await vodList({
          lan_code: mode,
          board_id: position === 'left' ? slideItem.clip_board_id : slideItem.replay_vod_board_id,
          type: 'vod',
          page_info: {
            row_count: count,
            number: 0,
          },
        });
        return vod.data.item
          .map((item) => ({
            id: item.vod_id,
            title: item.title,
            link: `/${mediaType}/${slideItem.program_id}/vod?id=${item.vod_id}&board=${item.board_id}`,
            thumbnail: item.image_url,
            duration: item.media_info.duration,
          }))
          .slice(rightCountPlus);
      case ASIDE_TYPES.EPISODE:
        const episode = await episodeList({
          lan_code: mode,
          board_id: position === 'left' ? slideItem.clip_board_id : slideItem.replay_vod_board_id,
          page_info: { row_count: count, number: 0 },
        });
        return episode.data.item
          .map((item) => ({
            id: item.episode_id,
            title: item.title,
            link: `/${mediaType}/${slideItem.program_id}/archive?id=${item.episode_id}&board=${item.board_id}`,
            thumbnail: item.image_url,
            duration: item.duration,
          }))
          .slice(rightCountPlus);
      default:
        return null;
    }
  };

  const { data: firstAsideData } = useQuery({
    queryKey: ['main', 'slide', 'aisde', 'first'],
    queryFn: async () => ({
      leftItems: await fetchAsideData('left', {
        sub_title: { left_menu_type: ASIDE_TYPES.NEWS_RECENT_VIDEO },
      }),
      rightItems: await fetchAsideData('right', {
        sub_title: { right_menu_type: ASIDE_TYPES.NEWS_TOPSTORY_LIST },
      }),
    }),

    enabled: !!slideData,
  });

  const { data: restAsideData } = useQuery({
    queryKey: ['main', 'slide', 'aisde', 'rest'],
    queryFn: async () => {
      const rest = await Promise.all(
        slideData.item.slice(1).map(async (item) => ({
          leftItems: await fetchAsideData('left', item),
          rightItems: await fetchAsideData('right', item),
        }))
      );
      return rest;
    },
    enabled: !!slideData,
  });

  const asideData = [firstAsideData || {}, ...(restAsideData || [{}])];

  useEffect(() => {
    if (slideData) {
      let arr = [];
      slideData?.item?.forEach((v, i) => {
        arr.push(v.bg_color);
      });
      setMainBg(arr);
    }

    const swiperEl = mainSlide.current;

    if (swiperEl && !swiperEl.querySelector('.swiper-button-auto')) {
      const pageEl = swiperEl.querySelector('.swiper-pagination');
      const button = document.createElement('span');
      button.setAttribute('class', 'swiper-button-auto');
      button.setAttribute('data-play', 'true');
      button.textContent = '';
      button.onclick = (e) => {
        if (swiperEl.swiper.autoplay.paused) {
          e.currentTarget.dataset.play = 'true';
          swiperEl.swiper.autoplay.run();
        } else {
          e.currentTarget.dataset.play = 'false';
          swiperEl.swiper.autoplay.pause();
        }
      };

      pageEl.appendChild(button);
    }
  }, [slideData]);

  return (
    <section
      id="mainSlider"
      className="main-slider"
      style={{ backgroundColor: `${mainSlideBg && mainSlideBg.includes('#') ? mainSlideBg : `#${mainSlideBg}`}` }}
    >
      <Swiper
        ref={mainSlide}
        autoHeight={true}
        pagination={{ clickable: true }}
        navigation
        onSlideChange={colorChange}
        autoplay={{
          delay: 7000,
          disableOnInteraction: false,
        }}
        threshold={1000}
      >
        {slideData?.item?.map((v, i) => {
          const image = v.slide_image.filter((el) => el.type === 'pc')?.[0];
          return (
            <SwiperSlide key={`mainSlide_${i}`}>
              <div className="slide">
                <div className="slide_img-wrap">
                  {v.description?.[0]?.text === 'url' ? (
                    <a href={v.url_link} className="slide_link" target="_blank" rel="noreferrer" title={anchorTitle}>
                      <p className="sr-only">프로그램 바로가기</p>
                    </a>
                  ) : (
                    <Link
                      to={
                        v.description?.[0]?.text === 'news'
                          ? nav['NEWS_MAIN'].path
                          : v.description?.[0]?.text === 'radio'
                          ? nav['RADIO_MAIN'].path + '/' + v.program_id
                          : v.description?.[0]?.text === 'tv'
                          ? nav['TV_MAIN'].path + '/' + v.program_id
                          : v.description?.[0]?.text === 'url' && v.url_link
                      }
                      className="slide_link"
                      title={anchorTitle}
                    >
                      <p className="sr-only">프로그램 바로가기</p>
                    </Link>
                  )}
                  <img className="slide_img" src={image?.url} alt="" />
                </div>
                <div className="slide_info">
                  <div className="slide_info_content">
                    <div className="clip-warp">
                      <div className="clip">
                        <p className="clip_title" style={{ color: '#' + v.font_color }}>
                          {v.sub_title?.left_text}
                        </p>
                        <div className="clip_info">
                          {asideData[i]?.leftItems?.map((vv) => (
                            <Link
                              key={vv.id}
                              to={vv.link}
                              className="clip_info_box"
                              title={anchorTitle}
                              onClick={() =>
                                logEvent(analytics, 'select_item', {
                                  item_list_name: `${vv.title}`,
                                  item_list_id: `news_${vv.id}`,
                                  items: [
                                    {
                                      item_id: `${vv.id}`,
                                      item_name: `${vv.title}`,
                                      item_brand: 'news',
                                    },
                                  ],
                                })
                              }
                            >
                              <div className="img-wrap ico_play">
                                <VideoThumbnail
                                  thumbnail={vv.thumbnail}
                                  duration={vv.duration}
                                  className="clip_info_img"
                                />
                              </div>
                              <div className="title" style={{ color: '#' + v.font_color }}>
                                {vv.title}
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="clip mode3">
                        <p className="clip_title" style={{ color: '#' + v.font_color }}>
                          {v.sub_title?.right_text}
                        </p>
                        <div
                          className="clip_info nav-style-1"
                          ref={(element) => {
                            rightSlideRef.current[i] = element;
                          }}
                        >
                          <Swiper className="slideSwiper" slidesPerView={3} spaceBetween={25} navigation={true}>
                            {asideData[i]?.rightItems?.map((vv) => (
                              <SwiperSlide
                                key={vv.id}
                                style={{ width: '!important calc((100%)/3)' }}
                                className="slideSwiper_content"
                              >
                                <Link
                                  to={vv.link}
                                  className="clip_info_box"
                                  title={anchorTitle}
                                  onClick={() =>
                                    logEvent(analytics, 'select_item', {
                                      item_list_name: `${vv.title}`,
                                      item_list_id: `news_${vv.id}`,
                                      items: [
                                        {
                                          item_id: `${vv.id}`,
                                          item_name: `${vv.title}`,
                                          item_brand: 'article',
                                        },
                                      ],
                                    })
                                  }
                                >
                                  <div className="img-wrap ico_play">
                                    <VideoThumbnail
                                      thumbnail={vv.thumbnail}
                                      duration={vv.duration}
                                      className="clip_info_img"
                                    />
                                  </div>
                                  <div className="title" style={{ color: '#' + v.font_color }}>
                                    {vv.title}
                                  </div>
                                </Link>
                              </SwiperSlide>
                            ))}
                          </Swiper>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </section>
  );
}

export default MainSlider;
